import { DefaultFilters } from "@/components/admin/fields/default-filters/default-filters";
import { SelectedFilters } from "@/lib/blocks/render-filters";
import { PropertyListingType } from "@/lib/types/marketsuite/datatypes/property";
import { PROPERTY_TYPE_OPTIONS } from "@/lib/types/marketsuite/property-type";
import { PropertyOverviewBlock } from "@/payload-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FILTER_TYPE_DEFINITIONS } from "@/lib/marketsuite/filters/registry";
import { buildFilterQuery, resolveDynamicFilters } from "@/lib/marketsuite/filters";
import { sortOptionValues } from "./results";
import { useUrl } from "@/contexts/frontend/url";
import { useRouter } from "next/navigation";

type PropertyOverviewFilterState = {
	filters: SelectedFilters;
	page: number;
	sort?: string;
	hasClientParams: boolean;
};

export type PropertyOverviewFilters = Omit<NonNullable<PropertyOverviewBlock["filters"]>, "defaultFilters"> & {
	defaultFilters?: DefaultFilters;
};

export const initialFilterState = (
	searchParams: URLSearchParams,
	filters: PropertyOverviewFilters,
	defaultFilters: Record<string, string> | undefined,
	listingType: PropertyListingType | undefined,
	partnerId?: string,
	defaultSort?: string | null,
): PropertyOverviewFilterState => {
	const availableFilterTypes = typeof filters.filterset !== "number" ? filters.filterset?.filters?.map((block) => block.blockType) : undefined;

	const f: SelectedFilters = {};

	for (const key of new Set([...Object.keys(defaultFilters ?? {}), ...searchParams.keys()])) {
		const definition = FILTER_TYPE_DEFINITIONS.find((definition) => definition.slug === key);

		if (!definition) continue;
		if (availableFilterTypes && !availableFilterTypes.includes(definition.slug)) continue;

		// Hidden filters are locked to their default value, so URL overrides are ignored.
		const searchValues = filters.defaultFilters?.hidden?.includes(key) ? [] : searchParams.getAll(key);

		if (definition.kind === "range") {
			const values = (searchValues[0] ?? defaultFilters?.[key] ?? "")
				.split(",")
				.map((value) => parseFloat(value))
				.filter((value) => !Number.isNaN(value));

			if (values.length > 0) {
				f[definition.slug] = values;
			}
		} else {
			const values = [...new Set([...(defaultFilters?.[key]?.split(",").filter(Boolean) ?? []), ...searchValues])];

			if (values.length > 0) {
				f[definition.slug] = values;
			}
		}
	}

	// The listing type is not a registry filter, so it is read from the URL separately.
	const listingTypes = searchParams
		.getAll("listing-type")
		.filter((value): value is PropertyListingType => PROPERTY_TYPE_OPTIONS.some((option) => option.value === value));

	if (listingTypes.length > 0) {
		f["listing-type"] = listingTypes;
	}

	if (typeof partnerId !== "undefined") {
		f["partner-id"] = [partnerId];
	}

	if (typeof listingType !== "undefined") {
		if ("listing-type" in f) {
			if (Array.isArray(f["listing-type"]) && !f["listing-type"].includes(listingType)) {
				f["listing-type"].push(listingType);
			}
		} else {
			f["listing-type"] = [listingType];
		}
	}

	const sort = searchParams.get("sort");

	return {
		filters: f,
		page: Number(searchParams.get("page")) || 1,
		sort: sort === "" || (sort && sortOptionValues.includes(sort)) ? sort : (defaultSort ?? ""),
		hasClientParams: searchParams.toString().length > 0,
	};
};

interface UsePropertyFilterStateProps {
	filters: PropertyOverviewFilters;
	onFilterChange: () => void;
	listingType?: PropertyListingType;
	partnerId?: string;
	defaultSort?: string | null;
	basePath: string;

	/**
	 * Suppresses syncing state to the URL. Set until the client takes over
	 * (e.g. first user interaction) to keep the initial URL untouched.
	 */
	disableUrlSync: boolean;
}

export const usePropertyFilterState = ({
	filters,
	onFilterChange,
	listingType,
	partnerId,
	defaultSort,
	basePath,
	disableUrlSync,
}: UsePropertyFilterStateProps) => {
	const router = useRouter();
	const { urlVariables } = useUrl();

	const defaultFilters = useMemo(
		() => resolveDynamicFilters(filters?.defaultFilters?.filters, urlVariables),
		[filters?.defaultFilters?.filters, urlVariables],
	);

	const [filterState, setFilterState] = useState<PropertyOverviewFilterState>(() =>
		initialFilterState(new URLSearchParams(), filters, defaultFilters, listingType, partnerId, defaultSort),
	);

	// The partner filter is locked to the page's partner and never appears in the URL.
	const hiddenFilters = useMemo(() => [...(filters.defaultFilters?.hidden ?? []), "partner-id"], [filters.defaultFilters?.hidden]);

	const filterQuery = useMemo(
		() =>
			buildFilterQuery({
				selectedFilters: filterState.filters,
				hiddenFilters,
				listingType,
				page: filterState.page,
				sort: filterState.sort === (defaultSort ?? "") ? undefined : filterState.sort,
			}),
		[filterState, hiddenFilters, listingType, defaultSort],
	);

	const hasAppliedFilters = useMemo(
		() => buildFilterQuery({ selectedFilters: filterState.filters, defaultFilters, hiddenFilters, listingType }).length > 0,
		[filterState.filters, defaultFilters, hiddenFilters, listingType],
	);

	useEffect(() => {
		if (disableUrlSync) return;

		router.push(filterQuery.length > 0 ? `${basePath}?${filterQuery}` : basePath, { scroll: false });
	}, [basePath, filterQuery, disableUrlSync, router]);

	const setSelectedFilters = useCallback(
		(newFilters: SelectedFilters) => {
			const resolvedFilters: SelectedFilters = {};

			for (const key of Object.keys(newFilters) as (keyof SelectedFilters)[]) {
				if (typeof newFilters[key] === "undefined") continue;

				// Listing types come from typed CMS fields and never contain variables.
				if (key === "listing-type") {
					resolvedFilters[key] = newFilters[key];
					continue;
				}

				const value = newFilters[key];

				if (!Array.isArray(value)) {
					resolvedFilters[key] = value;
					continue;
				}

				const strings = value.filter((item) => typeof item === "string");

				// Range values are numeric and never contain variables.
				if (strings.length === 0) {
					resolvedFilters[key] = value;
					continue;
				}

				resolvedFilters[key] = strings.map((item) => {
					if (item.startsWith(":")) {
						return urlVariables?.[item.replaceAll(":", "")] ?? item;
					}

					return item;
				});
			}

			onFilterChange();
			setFilterState((prev) => ({ ...prev, filters: resolvedFilters, page: 1, hasClientParams: true }));
		},
		[urlVariables],
	);

	return { setSelectedFilters, filterState, setFilterState, defaultFilters, hasAppliedFilters, filterQuery };
};
